import { motion } from "framer-motion";
import { colors, fonts } from "@/lib/theme";

interface HeaderProps {
  title?: string | null;
  description?: string | null;
}

/**
 * Option 3: Organic Brushstrokes
 * SVG paths draw in with organic curves
 */
export function OrganicStrokesHeader({ title, description }: HeaderProps) {
  const hasContent = title || description;

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (delay: number) => ({
      pathLength: 1,
      opacity: 0.3,
      transition: {
        pathLength: { duration: 2, ease: [0.4, 0, 0.2, 1], delay },
        opacity: { duration: 0.5, delay },
      },
    }),
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: colors.background }}
    >
      {/* Background strokes - use xMidYMid slice to avoid distortion on tall screens */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        <motion.path
          d="M10,30 Q30,10 50,30 T90,30"
          fill="none"
          stroke={colors.primary}
          strokeWidth="0.5"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          custom={0}
        />
        <motion.path
          d="M10,70 Q30,90 50,70 T90,70"
          fill="none"
          stroke={colors.primary}
          strokeWidth="0.5"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          custom={0.2}
        />
        <motion.path
          d="M20,20 Q40,50 20,80"
          fill="none"
          stroke={colors.accent}
          strokeWidth="0.5"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          custom={0.4}
          style={{ opacity: 0.25 }}
        />
        <motion.path
          d="M80,20 Q60,50 80,80"
          fill="none"
          stroke={colors.accent}
          strokeWidth="0.5"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          custom={0.4}
          style={{ opacity: 0.25 }}
        />
      </svg>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.6 }}
        className="text-center z-10 max-w-2xl"
      >
        {title && (
          <h2
            className="text-2xl sm:text-3xl md:text-5xl mb-2"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: 400,
              letterSpacing: "0.03em",
            }}
          >
            {title}
          </h2>
        )}

        {/* Title underline */}
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: 80 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.8 }}
          className="h-0.5 mx-auto mb-4 md:mb-6"
          style={{ background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)` }}
        />

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: 1.8,
            }}
          >
            {description}
          </p>
        )}

        {!hasContent && (
          <div
            style={{
              width: 60,
              height: 2,
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </div>
  );
}
